// Employee Store - Central HR Database
import { create } from 'zustand';
import { Employee, EmployeeState } from '../types/hr';
import { supabase } from '../api/supabase';

export const useEmployeeStore = create<EmployeeState>((set, get) => ({
  employees: [],
  selectedEmployee: null,
  loading: false,
  error: null,

  fetchEmployees: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          *,
          department:departments(*),
          manager:employees!employees_manager_id_fkey(id, name)
        `)
        .order('name');

      if (error) throw error;

      set({ 
        employees: (data || []).map(emp => ({
          id: emp.id,
          employeeId: emp.employee_id,
          name: emp.name,
          role: emp.role,
          departmentId: emp.department_id,
          department: emp.department,
          phone: emp.phone,
          email: emp.email,
          emergencyContact: emp.emergency_contact,
          emergencyPhone: emp.emergency_phone,
          joiningDate: emp.joining_date,
          probationEndDate: emp.probation_end_date,
          employmentType: emp.employment_type,
          managerId: emp.manager_id,
          manager: emp.manager,
          isActive: emp.is_active,
          category: emp.category,
          photoUrl: emp.photo_url,
          createdAt: emp.created_at,
          updatedAt: emp.updated_at,
        })),
        loading: false 
      });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  addEmployee: async (employee) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('employees')
        .insert({
          employee_id: employee.employeeId,
          name: employee.name,
          role: employee.role,
          department_id: employee.departmentId,
          phone: employee.phone,
          email: employee.email,
          emergency_contact: employee.emergencyContact,
          emergency_phone: employee.emergencyPhone,
          joining_date: employee.joiningDate,
          probation_end_date: employee.probationEndDate,
          employment_type: employee.employmentType,
          manager_id: employee.managerId,
          category: employee.category,
          photo_url: employee.photoUrl,
        })
        .select()
        .single();

      if (error) throw error;

      await get().fetchEmployees(); // Refresh list
      set({ loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateEmployee: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('employees')
        .update({
          name: updates.name,
          role: updates.role,
          department_id: updates.departmentId,
          phone: updates.phone,
          email: updates.email,
          emergency_contact: updates.emergencyContact,
          emergency_phone: updates.emergencyPhone,
          probation_end_date: updates.probationEndDate,
          employment_type: updates.employmentType,
          manager_id: updates.managerId,
          is_active: updates.isActive,
          photo_url: updates.photoUrl,
        })
        .eq('id', id);

      if (error) throw error;

      await get().fetchEmployees(); // Refresh list
      set({ loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteEmployee: async (id) => {
    set({ loading: true, error: null });
    try {
      // Soft delete - set is_active to false
      const { error } = await supabase
        .from('employees')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      await get().fetchEmployees(); // Refresh list
      set({ loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  selectEmployee: (employee) => {
    set({ selectedEmployee: employee });
  },
}));
